// web/src/components/horse/HorseForm.jsx
// Shared horse form for both Add (create) and Edit. Exposes every column the
// backend supports. Disciplines are intentionally omitted in v1 (per Taylor) —
// because the form never sends them, PATCH leaves any existing values untouched.
// Trainer assignment is deferred to Phase 2 (needs a member picker endpoint).
//
// Submits a snake_case payload matching the keys POST/PATCH /horses expect.
// Empty text fields are sent as null so editing can clear a value.

import { useState } from 'react';
import { prepareImageUpload, IMAGE_ACCEPT } from '../../lib/compressImage';
import { api } from '../../lib/api';

const SEXES = ['gelding', 'mare', 'stallion', 'colt', 'filly'];

const EMPTY = {
  name: '',
  show_name: '',
  stall: '',
  photo_url: '',
  sex: '',
  color: '',
  breed: '',
  birthdate: '',
  height_hh: '',
  markings: '',
  description: '',
  usef_number: '',
  ushja_number: '',
  usdf_number: '',
  microchip: '',
  insurance_co: '',
  insurance_policy: '',
  insurance_phone: '',
};

export default function HorseForm({
  mode = 'create',
  initial = null,
  onSubmit,
  submitting = false,
  submitError = null,
}) {
  const [f, setF] = useState(() => {
    const seed = { ...EMPTY };
    if (initial) {
      for (const k of Object.keys(EMPTY)) {
        if (initial[k] != null) seed[k] = String(initial[k]);
      }
    }
    return seed;
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  function set(key, val) {
    setF((cur) => ({ ...cur, [key]: val }));
  }

  const trimmedName = f.name.trim();
  const canSubmit = trimmedName.length > 0 && !submitting && !uploading;

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after an error
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      // Compress before upload. Straight to api.upload() ships whatever the
      // camera produced — on an iPhone that's a HEIC the Worker rejects with
      // 415, so cover photos from a phone simply failed.
      const toUpload = await prepareImageUpload(file);
      const res = await api.upload('/files', toUpload);
      set('photo_url', res.url);
    } catch (err) {
      setUploadError(friendlyUpload(err));
    } finally {
      setUploading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    const payload = { name: trimmedName };
    for (const k of Object.keys(EMPTY)) {
      if (k === 'name') continue;
      const v = typeof f[k] === 'string' ? f[k].trim() : f[k];
      payload[k] = v === '' ? null : v;
    }
    onSubmit(payload);
  }

  const submitLabel = submitting
    ? mode === 'edit'
      ? 'Saving…'
      : 'Adding…'
    : mode === 'edit'
      ? 'Save Changes'
      : 'Add Horse';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Section title="Basics">
        <Field label="Name" htmlFor="name">
          <input
            id="name"
            className="input w-full"
            value={f.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Cappuccino"
            autoFocus
            maxLength={120}
          />
        </Field>
        <Field label="Show name" optional htmlFor="show_name">
          <input
            id="show_name"
            className="input w-full"
            value={f.show_name}
            onChange={(e) => set('show_name', e.target.value)}
            placeholder="Caffeinated Dream"
          />
        </Field>
        <Field label="Stall" optional htmlFor="stall">
          <input
            id="stall"
            className="input w-full"
            value={f.stall}
            onChange={(e) => set('stall', e.target.value)}
            placeholder="12"
          />
        </Field>
        <PhotoField
          photoUrl={f.photo_url}
          uploading={uploading}
          uploadError={uploadError}
          onPick={handlePhoto}
          onClear={() => set('photo_url', '')}
        />
      </Section>

      <Section title="Identity">
        <Field label="Sex" optional htmlFor="sex">
          <select
            id="sex"
            className="input w-full capitalize"
            value={f.sex}
            onChange={(e) => set('sex', e.target.value)}
          >
            <option value="">—</option>
            {SEXES.map((s) => (
              <option key={s} value={s}>
                {cap(s)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Breed" optional htmlFor="breed">
          <input
            id="breed"
            className="input w-full"
            value={f.breed}
            onChange={(e) => set('breed', e.target.value)}
            placeholder="Warmblood"
          />
        </Field>
        <Field label="Color" optional htmlFor="color">
          <input
            id="color"
            className="input w-full"
            value={f.color}
            onChange={(e) => set('color', e.target.value)}
            placeholder="Bay"
          />
        </Field>
        <Field label="Markings" optional htmlFor="markings">
          <input
            id="markings"
            className="input w-full"
            value={f.markings}
            onChange={(e) => set('markings', e.target.value)}
            placeholder="Star, left hind sock"
          />
        </Field>
        <Field label="Date of birth" optional htmlFor="birthdate">
          <input
            id="birthdate"
            type="date"
            className="input w-full"
            value={f.birthdate}
            onChange={(e) => set('birthdate', e.target.value)}
          />
        </Field>
        <Field label="Height (hands)" optional htmlFor="height_hh">
          <input
            id="height_hh"
            className="input w-full"
            value={f.height_hh}
            onChange={(e) => set('height_hh', e.target.value)}
            placeholder="16.2"
            inputMode="decimal"
          />
        </Field>
        <Field label="Notes" optional htmlFor="description">
          <textarea
            id="description"
            className="input w-full"
            rows={3}
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Anything worth knowing at a glance."
          />
        </Field>
      </Section>

      <Section title="Registrations">
        <Field label="USEF #" optional htmlFor="usef_number">
          <input
            id="usef_number"
            className="input w-full font-mono"
            value={f.usef_number}
            onChange={(e) => set('usef_number', e.target.value)}
          />
        </Field>
        <Field label="USHJA #" optional htmlFor="ushja_number">
          <input
            id="ushja_number"
            className="input w-full font-mono"
            value={f.ushja_number}
            onChange={(e) => set('ushja_number', e.target.value)}
          />
        </Field>
        <Field label="USDF #" optional htmlFor="usdf_number">
          <input
            id="usdf_number"
            className="input w-full font-mono"
            value={f.usdf_number}
            onChange={(e) => set('usdf_number', e.target.value)}
          />
        </Field>
        <Field label="Microchip #" optional htmlFor="microchip">
          <input
            id="microchip"
            className="input w-full font-mono"
            value={f.microchip}
            onChange={(e) => set('microchip', e.target.value)}
          />
        </Field>
      </Section>

      <Section title="Insurance">
        <Field label="Company" optional htmlFor="insurance_co">
          <input
            id="insurance_co"
            className="input w-full"
            value={f.insurance_co}
            onChange={(e) => set('insurance_co', e.target.value)}
          />
        </Field>
        <Field label="Policy #" optional htmlFor="insurance_policy">
          <input
            id="insurance_policy"
            className="input w-full font-mono"
            value={f.insurance_policy}
            onChange={(e) => set('insurance_policy', e.target.value)}
          />
        </Field>
        <Field label="Phone" optional htmlFor="insurance_phone">
          <input
            id="insurance_phone"
            type="tel"
            className="input w-full"
            value={f.insurance_phone}
            onChange={(e) => set('insurance_phone', e.target.value)}
          />
        </Field>
      </Section>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="card p-5">
      <h2 className="font-medium mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-700 mb-1">
        {label}
        {optional && <span className="font-normal text-ink-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

function PhotoField({ photoUrl, uploading, uploadError, onPick, onClear }) {
  return (
    <div>
      <span className="block text-sm font-medium text-ink-700 mb-1">
        Photo <span className="font-normal text-ink-400">(optional)</span>
      </span>
      <div className="flex items-center gap-3">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="w-16 h-16 rounded-lg object-cover border border-ink-100"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-ink-100 shrink-0" />
        )}
        <div className="min-w-0">
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            onChange={onPick}
            disabled={uploading}
            className="text-sm text-ink-600"
          />
          {uploading && <p className="text-xs text-ink-400 mt-1">Uploading…</p>}
          {uploadError && <p className="text-xs text-red-600 mt-1">{uploadError}</p>}
          {photoUrl && !uploading && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-ink-500 underline mt-1"
            >
              Remove photo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function cap(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function friendlyUpload(err) {
  const code = err?.body?.error || err?.message;
  switch (code) {
    case 'unsupported':
    case 'unsupported_type':
      return 'That file type isn’t supported. Use a JPG, PNG, WebP, GIF, or an iPhone photo.';
    case 'input_too_large':
      return 'That file is over 50 MB — is it a video?';
    case 'file_too_large':
      return 'That image is still too large after compressing (max 8 MB).';
    case 'files_not_configured':
      return 'Photo uploads aren’t set up yet. Save without a photo for now.';
    case 'forbidden':
      return 'You don’t have permission to upload photos.';
    default:
      return 'Upload failed. Try again.';
  }
}
